import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { workspaceService } from "../services/workspaceService";

const WorkspaceContext = createContext(null);
const ACTIVE_KEY = "gantt-pro.active-workspace";

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [context, setContext] = useState({ organizations: [], workspaces: [], default_workspace_id: null });
  const [contents, setContents] = useState({ projects: [], diagrams: [] });
  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState(() => Number(localStorage.getItem(ACTIVE_KEY)) || null);
  const [loading, setLoading] = useState(Boolean(user));

  const refresh = useCallback(async () => {
    if (!user) {
      setContext({ organizations: [], workspaces: [], default_workspace_id: null });
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      // The context establishes the personal workspace on first use. Load it
      // before content so initial requests cannot compete for bootstrap work.
      const data = await workspaceService.context();
      const contentData = await workspaceService.list();
      setContext(data);
      setContents(contentData);
      setActiveWorkspaceIdState((current) => {
        const available = data.workspaces.some((workspace) => workspace.id === current && !workspace.is_archived);
        return available ? current : data.default_workspace_id || data.workspaces.find((workspace) => !workspace.is_archived)?.id || null;
      });
      return data;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const refreshContents = useCallback(async () => {
    if (!user) return { projects: [], diagrams: [] };
    const data = await workspaceService.list();
    setContents(data);
    return data;
  }, [user]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (activeWorkspaceId) localStorage.setItem(ACTIVE_KEY, String(activeWorkspaceId));
  }, [activeWorkspaceId]);

  const setActiveWorkspaceId = useCallback((value) => setActiveWorkspaceIdState(Number(value) || null), []);
  const upsertWorkspace = useCallback((workspace) => {
    setContext((current) => ({
      ...current,
      workspaces: [...current.workspaces.filter((item) => item.id !== workspace.id), workspace]
        .sort((left, right) => left.name.localeCompare(right.name, "es")),
    }));
    setActiveWorkspaceIdState(workspace.id);
  }, []);
  const removeWorkspace = useCallback((workspaceId) => {
    setContext((current) => ({
      ...current,
      workspaces: current.workspaces.filter((item) => item.id !== workspaceId),
    }));
    setContents((current) => ({
      projects: current.projects.filter((item) => item.workspace_id !== workspaceId),
      diagrams: current.diagrams.filter((item) => item.workspace_id !== workspaceId),
    }));
    setActiveWorkspaceIdState((current) => current === workspaceId ? null : current);
  }, []);
  const activeWorkspace = context.workspaces.find((workspace) => workspace.id === activeWorkspaceId) || null;
  const activeOrganization = context.organizations.find((organization) => organization.id === activeWorkspace?.organization_id) || null;
  const value = useMemo(() => ({
    ...context, contents, activeWorkspaceId, activeWorkspace, activeOrganization, loading,
    setActiveWorkspaceId, upsertWorkspace, removeWorkspace, refresh, refreshContents,
  }), [context, contents, activeWorkspaceId, activeWorkspace, activeOrganization, loading, setActiveWorkspaceId, upsertWorkspace, removeWorkspace, refresh, refreshContents]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export const useWorkspace = () => useContext(WorkspaceContext);
